import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Sprout,
  TrendingUp,
  TreePine,
  Rocket,
  BarChart3,
  Lightbulb
} from 'lucide-react';
import SuccessCelebration from '../shared/SuccessCelebration';
import MorphingButton from '../shared/MorphingButton';

const ExpectedUsage = ({ data, onNext, onBack }) => {
  const [expectedResponses, setExpectedResponses] = useState(data.expectedResponses || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const responseOptions = [
    {
      value: 'under-100',
      label: '< 100',
      subtitle: 'Just getting started',
      icon: Sprout,
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
    },
    {
      value: '100-1000',
      label: '100 – 1,000',
      subtitle: 'Growing audience',
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
    },
    {
      value: '1k-10k',
      label: '1k – 10k',
      subtitle: 'Established presence',
      icon: TreePine,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
    },
    {
      value: '10k-plus',
      label: '10k+',
      subtitle: 'Large scale',
      icon: Rocket,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
    }
  ];

  const handleSubmit = async () => {
    if (!expectedResponses) {
      setError('Please select your expected monthly responses');
      return;
    }

    setIsSubmitting(true);

    // Trigger celebration
    setShowCelebration(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1200));

    onNext({ expectedResponses });
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', padding: '0 clamp(16px, 3vw, 0px)' }}>
      {/* Success Celebration */}
      {showCelebration && <SuccessCelebration type="firework" />}
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)', textAlign: 'center' }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 15 }}
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <BarChart3 size={Math.min(48, window.innerWidth * 0.09)} color="#3B82F6" />
        </motion.div>
        <h2
          style={{
            fontSize: 'clamp(24px, 5vw, 32px)',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px'
          }}
        >
          Expected monthly responses
        </h2>
        <p style={{ fontSize: 'clamp(14px, 3vw, 16px)', color: '#6B7280', lineHeight: '1.5' }}>
          This helps us optimize your analytics and ensure smooth performance
        </p>
      </motion.div>

      {/* Response Options */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'clamp(12px, 2vw, 16px)',
          marginBottom: 'clamp(24px, 4vw, 32px)'
        }}
      >
        {responseOptions.map((option, index) => {
          const isSelected = expectedResponses === option.value;
          const IconComponent = option.icon;
          return (
            <motion.button
              key={option.value}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: 0.3 + index * 0.1,
                duration: 0.4,
                type: 'spring',
                stiffness: 200,
                damping: 20
              }}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setExpectedResponses(option.value);
                setError('');
              }}
              style={{
                padding: '0',
                border: `3px solid ${
                  isSelected ? '#3B82F6' : error ? '#EF4444' : '#E5E7EB'
                }`,
                borderRadius: '20px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit',
                textAlign: 'left',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#FFFFFF',
                boxShadow: isSelected
                  ? '0 12px 28px rgba(59, 130, 246, 0.25)'
                  : '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              {/* Gradient Header */}
              <div
                style={{
                  background: isSelected ? option.gradient : '#E5E7EB',
                  padding: 'clamp(16px, 3vw, 20px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease'
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 'clamp(20px, 4vw, 24px)',
                      fontWeight: '700',
                      color: '#FFFFFF',
                      marginBottom: '4px'
                    }}
                  >
                    {option.label}
                  </div>
                  <div
                    style={{
                      fontSize: 'clamp(12px, 2.5vw, 13px)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: '500'
                    }}
                  >
                    {option.subtitle}
                  </div>
                </div>
                <IconComponent size={Math.min(32, window.innerWidth * 0.06)} color="#FFFFFF" />
              </div>

              {/* Check indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M3 9L7 13L15 5"
                      stroke="#3B82F6"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </motion.div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            color: '#EF4444',
            fontSize: '13px',
            marginTop: '-16px',
            marginBottom: '16px',
            textAlign: 'center'
          }}
        >
          {error}
        </motion.p>
      )}

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        style={{
          padding: 'clamp(14px, 2vw, 16px) clamp(16px, 3vw, 20px)',
          backgroundColor: 'rgba(59, 130, 246, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          marginBottom: '32px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}
      >
        <Lightbulb size={20} color="#3B82F6" />
        <p
          style={{
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            color: '#374151',
            lineHeight: '1.6',
            margin: 0
          }}
        >
          Don't worry, you can always upgrade or change your plan later based on your actual
          usage
        </p>
      </motion.div>

      {/* Navigation Buttons */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.4 }}
        style={{
          display: 'flex',
          gap: 'clamp(10px, 2vw, 12px)',
          marginTop: 'clamp(32px, 5vw, 40px)',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <button
          onClick={onBack}
          disabled={isSubmitting}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(20px, 3vw, 28px)',
            backgroundColor: '#FFFFFF',
            color: '#374151',
            border: `2px solid ${isSubmitting ? '#E5E7EB' : '#E5E7EB'}`,
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit',
            opacity: isSubmitting ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.target.style.borderColor = '#374151';
              e.target.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.target.style.borderColor = '#E5E7EB';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          ← Back
        </button>

        <MorphingButton
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? 'Completing Setup...' : 'Complete Setup 🎉'}
        </MorphingButton>
      </motion.div>
    </div>
  );
};

export default ExpectedUsage;